(function () {
  if (customElements.get('eterea-ship-door')) return;

  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const seg = (p, a, b) => clamp((p - a) / (b - a), 0, 1);
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);
  const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  // Flight timeline over the section's scroll (0 → 1)
  const LAND_END = 0.32;
  const ZOOM_START = 0.34;
  const ZOOM_END = 0.8;
  const FADE_START = 0.72;
  const READY = 0.8;
  // Two-part playback when the visitor scrolls down into the scene (ms):
  // landing, a pause with a "keep scrolling" sign, then the fly-in to the door
  const LAND_MS = 1700;
  const LAND_MS_MOBILE = 1500;
  const ZOOM_MS = 1500;
  const ZOOM_MS_MOBILE = 1200;

  class EtereaShipDoor extends HTMLElement {
    connectedCallback() {
      this.openBtn = this.querySelector('[data-esd-open]');
      this.closeBtn = this.querySelector('[data-esd-close]');
      this.interior = this.querySelector('[data-esd-interior]');
      this.card = this.querySelector('[data-esd-card]');
      this.hint = this.querySelector('[data-esd-hint]');
      this.hintText = this.hint ? this.hint.textContent : '';
      if (!this.openBtn) return;
      this.setInterior(false);
      this.openBtn.addEventListener('click', () => this.open());
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
      const alt = this.querySelector('[data-esd-open-alt]');
      if (alt) alt.addEventListener('click', () => this.open());
      this.setupFlight();
      // The hologram projects only when the door is on screen and the ship isn't flying in
      this.io = new IntersectionObserver(([e]) => {
        this.inView = e.isIntersecting;
        this.updateHolo();
        this.updateCue(false);
      }, { threshold: 0.35 });
      this.io.observe(this.querySelector('.esd__stage') || this);
    }

    disconnectedCallback() {
      this.stopFlight();
      if (this.io) this.io.disconnect();
    }

    setInterior(on) {
      if (!this.interior) return;
      if (on) this.interior.removeAttribute('inert');
      else this.interior.setAttribute('inert', '');
      this.interior.setAttribute('aria-hidden', on ? 'false' : 'true');
    }

    // "Keep scrolling" sign: shows when the visitor stops inside the scene, hides while scrolling
    updateCue(moving) {
      if (!this.cue) return;
      clearTimeout(this.cueT);
      const want = this.flying && this.inView && this.current != null && this.current < READY - 0.02 && !this.play && !this.classList.contains('is-open');
      if (!want) {
        this.cue.classList.remove('is-on');
        return;
      }
      if (moving) {
        this.cue.classList.remove('is-on');
        this.cueT = setTimeout(() => this.updateCue(false), 900);
        return;
      }
      const text = this.current < LAND_END - 0.005 ? this.cue.dataset.land : this.cue.dataset.door;
      if (this.cueText && text && this.cueText.textContent !== text) this.cueText.textContent = text;
      this.cue.classList.add('is-on');
    }

    updateHolo() {
      const on = !!this.inView && !this.classList.contains('is-flying') && !this.classList.contains('is-open');
      this.classList.toggle('holo-on', on);
    }

    open() {
      if (this.classList.contains('is-open')) return;
      if (this.classList.contains('is-flying')) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.openBtn.setAttribute('aria-expanded', 'true');
      if (this.hint && this.dataset.granted) this.hint.textContent = this.dataset.granted;
      this.classList.add('is-unlocking');
      this.classList.add('holo-on');
      clearTimeout(this.t);
      this.t = setTimeout(() => {
        this.classList.add('is-open');
        this.setInterior(true);
        if (this.card) this.card.focus({ preventScroll: true });
        this.t2 = setTimeout(() => this.classList.add('is-inside'), reduce ? 0 : 1100);
      }, reduce ? 0 : 380);
    }

    close(silent) {
      clearTimeout(this.t);
      clearTimeout(this.t2);
      this.classList.remove('is-open', 'is-unlocking', 'is-inside');
      this.openBtn.setAttribute('aria-expanded', 'false');
      if (this.hint) this.hint.textContent = this.hintText;
      this.setInterior(false);
      this.updateHolo();
      if (!silent) this.openBtn.focus({ preventScroll: true });
    }

    /* ---------- Flight (desktop and phone) ---------- */

    setupFlight() {
      this.scene = this.querySelector('.esd__scene');
      if (!this.scene || this.dataset.flight !== 'true') return;
      this.track = this.querySelector('.esd__track');
      this.stage = this.querySelector('.esd__stage');
      this.sky = this.scene.querySelector('.esd__sky');
      this.world = this.scene.querySelector('.esd__world');
      this.ship = this.scene.querySelector('.esd__ship');
      this.flames = this.scene.querySelector('.esd__flames');
      this.dust = this.scene.querySelector('.esd__dust');
      this.shadow = this.scene.querySelector('.esd__shipshadow');
      this.rings = Array.from(this.scene.querySelectorAll('.esd__rings'));
      this.planet = this.scene.querySelector('.esd__planet');
      this.shipDoor = this.scene.querySelector('.esd__ship-door');
      this.frame = this.querySelector('.esd__frame');
      this.worldSvg = this.world ? this.world.querySelector('svg') : null;
      this.cue = this.querySelector('[data-esd-cue]');
      this.cueText = this.cue ? this.cue.querySelector('.esd__cue-text') : null;

      this.mq = window.matchMedia('(prefers-reduced-motion: no-preference)');
      this.onMq = () => (this.mq.matches ? this.startFlight() : this.stopFlight());
      this.mq.addEventListener('change', this.onMq);
      this.onMq();
    }

    startFlight() {
      if (this.flying) return;
      this.flying = true;
      this.classList.add('has-flight');
      this.current = null;
      this.onScroll = () => {
        this.requestTick();
        // The scene's own page positioning doesn't count as the visitor scrolling
        const own = this.play || (this.playEndedAt && performance.now() - this.playEndedAt < 200) || (this.holdUntil && performance.now() < this.holdUntil);
        if (!own) this.updateCue(true);
      };
      this.onResize = () => {
        this.measure();
        this.requestTick();
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      requestAnimationFrame(() => {
        this.measure();
        this.requestTick();
      });
    }

    stopFlight() {
      if (!this.flying) return;
      this.flying = false;
      this.classList.remove('has-flight', 'is-flying');
      window.removeEventListener('scroll', this.onScroll);
      this.play = null;
      document.documentElement.style.overflow = '';
      clearTimeout(this.cueT);
      if (this.cue) this.cue.classList.remove('is-on');
      window.removeEventListener('resize', this.onResize);
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }

    // Phones get a portrait framing: the ship centered, planet peeking at the top
    setFraming() {
      const mobile = window.innerWidth < 990;
      if (mobile === this.mobile) return;
      this.mobile = mobile;
      this.classList.toggle('is-mobile-flight', mobile);
      if (this.worldSvg) {
        this.worldSvg.setAttribute('viewBox', mobile ? '530 -260 540 1160' : '0 0 1600 900');
        this.worldSvg.setAttribute('preserveAspectRatio', mobile ? 'xMidYMax meet' : 'xMidYMid slice');
      }
      if (this.sky) this.sky.setAttribute('viewBox', mobile ? '820 -40 560 1000' : '0 0 1600 900');
    }

    measure() {
      this.setFraming();
      if (!this.world || !this.shipDoor || !this.frame) return;
      // While the door is open the exterior is scaled, so the real door can't be measured
      if (this.classList.contains('is-open') || this.classList.contains('is-inside')) {
        this.needsMeasure = true;
        return;
      }
      this.needsMeasure = false;
      const prevWorld = this.world.style.transform;
      const prevShip = this.ship.style.transform;
      this.world.style.transform = 'none';
      this.ship.style.transform = 'none';
      const s = this.stage.getBoundingClientRect();
      const d = this.shipDoor.getBoundingClientRect();
      const f = this.frame.getBoundingClientRect();
      this.world.style.transform = prevWorld;
      this.ship.style.transform = prevShip;
      if (!d.height || !f.height) return;
      this.door0 = { x: d.left - s.left + d.width / 2, y: d.top - s.top + d.height / 2, h: d.height, w: d.width };
      // Layout position of the real door, ignoring any transform still animating on it
      let fx = 0;
      let fy = 0;
      let el = this.frame;
      while (el && el !== this.stage && this.stage.contains(el)) {
        fx += el.offsetLeft;
        fy += el.offsetTop;
        el = el.offsetParent;
      }
      if (el === this.stage && this.frame.offsetHeight) {
        this.target = { x: fx + this.frame.offsetWidth / 2, y: fy + this.frame.offsetHeight / 2, h: this.frame.offsetHeight, w: this.frame.offsetWidth };
      } else {
        this.target = { x: f.left - s.left + f.width / 2, y: f.top - s.top + f.height / 2, h: f.height, w: f.width };
      }
      // On a phone the real door is narrower than the ship's: match widths and line up the bottoms
      if (this.mobile) {
        const sc = this.target.w / this.door0.w;
        const bottom = this.target.y + this.target.h / 2;
        this.target = { x: this.target.x, y: bottom - (this.door0.h * sc) / 2, h: this.door0.h * sc, w: this.target.w };
      }
      this.stageH = s.height;
    }

    // Coming back from the open door: measure again and force the scene to redraw
    repaint() {
      if (this.needsMeasure) this.measure();
      if (this.scene) {
        this.scene.style.display = 'none';
        void this.scene.offsetHeight;
        this.scene.style.display = '';
      }
      this.requestTick();
    }

    progress() {
      const r = this.track.getBoundingClientRect();
      const run = r.height - window.innerHeight;
      if (run <= 0) return 1;
      return clamp(-r.top / run, 0, 1);
    }

    requestTick() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => this.tick());
    }

    // Moves the page without making the sticky header pop in (it reveals on any upward nudge)
    parkScroll(top) {
      const header = document.querySelector('sticky-header');
      if (header && Math.round(top) < window.scrollY) header.dispatchEvent(new Event('preventHeaderReveal'));
      window.scrollTo({ top, behavior: 'instant' });
    }

    scrollFor(p) {
      const r = this.track.getBoundingClientRect();
      const run = r.height - window.innerHeight;
      return Math.round(window.scrollY + r.top + run * p);
    }

    // Plays the scene to "to" at a fixed speed, keeping the page parked where the scene is.
    // The page is only ever positioned, never locked: no touch blocking, no overflow tricks.
    startPlay(to) {
      const landing = to <= LAND_END;
      const ms = landing ? (this.mobile ? LAND_MS_MOBILE : LAND_MS) : this.mobile ? ZOOM_MS_MOBILE : ZOOM_MS;
      const span = landing ? LAND_END : 1 - LAND_END;
      this.play = { to, rate: span / ms, until: performance.now() + ms + 1500 };
      this.updateCue(false);
      this.lastT = 0;
      this.requestTick();
    }

    endPlay() {
      const landed = this.play && this.play.to <= LAND_END;
      this.play = null;
      this.lastT = 0;
      this.playEndedAt = performance.now();
      // After touchdown, hold the pause briefly so leftover momentum doesn't skip it
      if (landed) {
        this.holdUntil = this.playEndedAt + 600;
        this.requestTick();
      }
      this.updateCue(false); // at the pause this shows "keep scrolling to reach the door"
    }

    tick() {
      this.raf = null;
      if (!this.flying) return;
      const now = performance.now();
      const dt = this.lastT ? Math.min(now - this.lastT, 50) : 16;
      this.lastT = now;
      const live = this.progress();

      if (this.current == null) {
        // First frame (page loaded here): show where the visitor is
        this.current = live;
        this.lastGoal = live;
        this.render(live);
        this.lastT = 0;
        this.updateCue(false);
        return;
      }

      if (this.play) {
        // Scrolling up always wins: stop and let the page go
        if (live < this.current - 0.03 || now > this.play.until) {
          this.play = null;
          this.lastT = 0;
          this.requestTick();
          return;
        }
        const to = this.play.to;
        this.current = Math.min(to, this.current + dt * this.play.rate);
        this.render(this.current);
        this.parkScroll(this.scrollFor(this.current));
        this.lastGoal = this.current;
        if (this.current >= to) this.endPlay();
        else this.requestTick();
        return;
      }

      const jumped = Math.abs(live - (this.lastGoal == null ? live : this.lastGoal)) >= 1; // anchor link, Home/End
      this.lastGoal = live;
      if (jumped) {
        this.current = live;
        this.render(live);
        this.lastT = 0;
        return;
      }

      if (this.holdUntil && now < this.holdUntil && live > this.current && live < 1) {
        this.parkScroll(this.scrollFor(this.current));
        this.requestTick();
        return;
      }
      if (live > this.current + 0.002) {
        if (live < 1 && this.current < LAND_END - 0.001) {
          this.startPlay(LAND_END); // part 1: landing
          return;
        }
        if (live < 1 && this.current < 1) {
          this.startPlay(1); // part 2: fly-in to the door
          return;
        }
      }
      // Going up (or already past the section): follow the scroll freely
      this.current += (live - this.current) * 0.25;
      if (Math.abs(live - this.current) < 0.0005) this.current = live;
      this.render(this.current);
      if (this.current !== live) this.requestTick();
      else this.lastT = 0;
    }

    render(p) {
      try {
        this.draw(p);
      } catch (err) {
        // If anything goes wrong, drop the effect and leave a normal, scrollable page
        console.warn('eterea-ship-door: flight disabled', err);
        this.stopFlight();
      }
    }

    draw(p) {
      if (!this.door0) this.measure();
      if (!this.door0) return;

      // 1. Landing: the ship descends onto the platform (reverse = take-off)
      const land = easeOut(seg(p, 0, LAND_END));
      const lift = (1 - land) * (this.mobile ? 1900 : 1000); // SVG units (taller framing on phones)
      const sway = Math.sin(land * Math.PI * 2) * (1 - land) * 1.5;
      this.ship.style.transform = `translateY(${-lift}px) rotate(${sway}deg)`;
      // Fully gone at the very top, flames included
      this.ship.style.visibility = land <= 0.001 ? "hidden" : "";
      const thrust = 1 - seg(p, LAND_END - 0.04, LAND_END + 0.01);
      this.flames.style.opacity = thrust.toFixed(3);
      this.flames.style.transform = `scaleY(${(0.35 + thrust * 0.65 + Math.random() * 0.06 * thrust).toFixed(3)})`;
      const touch = seg(p, LAND_END - 0.08, LAND_END) * (1 - seg(p, LAND_END, LAND_END + 0.1));
      this.dust.style.opacity = (touch * 0.9).toFixed(3);
      this.dust.style.transform = `scale(${(0.6 + touch * 0.6).toFixed(3)}, ${(0.6 + touch * 0.5).toFixed(3)})`;

      // 2. Fly-in: the camera zooms to the ship's door until it matches the real door
      const z = easeInOut(seg(p, ZOOM_START, ZOOM_END));
      const sEnd = this.target.h / this.door0.h;
      const sc = Math.exp(Math.log(sEnd) * z);
      const px = this.door0.x + (this.target.x - this.door0.x) * z;
      const py = this.door0.y + (this.target.y - this.door0.y) * z;
      this.world.style.transform = `translate(${(px - sc * this.door0.x).toFixed(2)}px, ${(py - sc * this.door0.y).toFixed(2)}px) scale(${sc.toFixed(4)})`;
      const tilt = -14 + p * 22;
      const squash = 1 + Math.sin(p * Math.PI) * 0.35;
      this.rings.forEach((r) => { r.style.transform = `rotate(${tilt.toFixed(2)}deg) scaleY(${squash.toFixed(3)})`; });
      if (this.planet) this.planet.style.transform = `rotate(${(tilt + p * 40).toFixed(2)}deg)`;
      if (this.shadow) {
        this.shadow.style.opacity = (land * 0.9).toFixed(3);
        this.shadow.style.transform = `scale(${(0.35 + land * 0.65).toFixed(3)})`;
      }
      this.sky.style.transform = `translateY(${(z * 0.08 * this.stageH).toFixed(1)}px) scale(${(1 + z * 0.35).toFixed(3)})`;

      // 3. Hand-off to the real door
      this.scene.style.opacity = (1 - seg(p, FADE_START, ZOOM_END)).toFixed(3);
      const flying = p < READY;
      this.classList.toggle('is-flying', flying);
      this.updateHolo();
      if (flying && this.classList.contains('is-open')) {
        this.close(true);
        this.repaint();
      }
    }
  }

  customElements.define('eterea-ship-door', EtereaShipDoor);
})();
